import { useEffect, useState, useMemo } from 'react'
import { UseFormReturn } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { FormField, FormItem, FormLabel, FormControl, FormMessage } from '@/components/ui/form'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import { RunbookTable } from './RunbookTable'
import { objectsApi } from '../api'
import { calculateRunbookTimes } from '../utils/calculateRunbookTimes'
import { useRunbook } from '../hooks'
import type { ObjectFormData } from '../schema'
import type { RunbookTask } from '@/types/entities'

interface RunbookTabProps {
    form: UseFormReturn<ObjectFormData>
    isEditing?: boolean
    objectID?: string | null
    scenario?: string | null
}

export function RunbookTab({ form, isEditing = false, objectID = null, scenario = null }: RunbookTabProps) {
    const { t } = useTranslation()
    const masterRunbook = form.watch('MasterRunbook')
    const runbookTasks = form.watch('RunbookTasks') || []
    const [runbookInfo, setRunbookInfo] = useState<{
        information: string
        engineerRTO: number | null
        numberOfTasks: number | null
    } | null>(null)
    const [localTasks, setLocalTasks] = useState<RunbookTask[]>([])
    
    // Sync localTasks with form state
    useEffect(() => {
        const tasks = form.getValues('RunbookTasks') || []
        console.log('[RunbookTab] Syncing localTasks from form:', tasks)
        setLocalTasks(tasks)
    }, [runbookTasks, form])

    const { data: masterRunbooksData } = useQuery({
        queryKey: ['masterRunbooks'],
        queryFn: async () => {
            const response = await objectsApi.getMasterRunbooks()
            return response.data?.data || response.data || []
        },
        enabled: !isEditing, // Only fetch when creating (not editing)
    })

    // When editing: Load runbook tasks from Runbook_Object table
    const { data: objectRunbookTasks, isLoading: isLoadingObjectRunbook } = useRunbook(
        isEditing ? objectID : null,
        scenario || undefined
    )

    // When editing: Load master runbook info from the assigned master runbook
    useEffect(() => {
        if (isEditing && masterRunbook) {
            objectsApi.getMasterRunbookInfo(masterRunbook)
                .then((response) => {
                    const data = response.data?.data || response.data
                    setRunbookInfo({
                        information: data.information || '',
                        engineerRTO: data.engineerRTO || null,
                        numberOfTasks: data.numberOfTasks || null,
                    })
                })
                .catch((error) => {
                    console.error('Error fetching master runbook info:', error)
                    setRunbookInfo(null)
                })
        }
    }, [isEditing, masterRunbook])

    // When editing: Load tasks from Runbook_Object into form
    useEffect(() => {
        if (isEditing && objectRunbookTasks && !isLoadingObjectRunbook) {
            console.log('[RunbookTab] Loading tasks from Runbook_Object:', objectRunbookTasks)
            const tasks: RunbookTask[] = objectRunbookTasks.map((task: any) => ({
                id: task.id || null,
                taskNo: task.taskNo || null,
                task: task.task || '',
                commands: task.commands || '',
                duration: task.duration || 0,
                taskOffset: task.taskOffset !== null && task.taskOffset !== undefined ? task.taskOffset : null,
                startTime: null, // Deprecated: not saved, calculated from taskOffset if needed for display
                finishTime: null, // Deprecated: not saved, calculated from taskOffset if needed for display
                dependencies: task.dependencies || '',
            }))
            
            // Calculate TaskOffset for the tasks
            const tasksWithOffsets = calculateRunbookTimes(tasks)
            console.log('[RunbookTab] Tasks with calculated TaskOffset:', tasksWithOffsets)
            form.setValue('RunbookTasks', tasksWithOffsets, { shouldValidate: false, shouldDirty: false })
            setLocalTasks(tasksWithOffsets)
        }
    }, [isEditing, objectRunbookTasks, isLoadingObjectRunbook, form])

    // When creating: Load master runbook info and tasks when master runbook is selected
    useEffect(() => {
        if (!isEditing && masterRunbook) {
            objectsApi.getMasterRunbookInfo(masterRunbook)
                .then((response) => {
                    const data = response.data?.data || response.data
                    setRunbookInfo({
                        information: data.information || '',
                        engineerRTO: data.engineerRTO || null,
                        numberOfTasks: data.numberOfTasks || null,
                    })
                    
                    // Convert master runbook tasks to RunbookTask format and load into form
                    console.log('[RunbookTab] Runbook data received:', data)
                    console.log('[RunbookTab] Tasks from API:', data.tasks)
                    if (data.tasks && data.tasks.length > 0) {
                        const tasks: RunbookTask[] = data.tasks.map((task: any) => ({
                            id: null, // New tasks don't have IDs yet
                            taskNo: task.taskNo || null,
                            task: task.task || '',
                            commands: task.commands || '',
                            duration: task.duration || 0,
                            taskOffset: null, // Will be calculated
                            startTime: null, // Deprecated: not saved, calculated from taskOffset if needed for display
                            finishTime: null, // Deprecated: not saved, calculated from taskOffset if needed for display
                            dependencies: task.dependencies || (task.taskNo === 1 ? '' : '1'),
                        }))
                        
                        console.log('[RunbookTab] Converted tasks:', tasks)
                        
                        // Calculate TaskOffset for the tasks
                        const tasksWithOffsets = calculateRunbookTimes(tasks)
                        console.log('[RunbookTab] Tasks with calculated TaskOffset:', tasksWithOffsets)
                        console.log('[RunbookTab] TaskOffset values:', tasksWithOffsets.map(t => ({ taskNo: t.taskNo, taskOffset: t.taskOffset, duration: t.duration, dependencies: t.dependencies })))
                        form.setValue('RunbookTasks', tasksWithOffsets, { shouldValidate: false, shouldDirty: true })
                        setLocalTasks(tasksWithOffsets)
                        console.log('[RunbookTab] Form RunbookTasks set to:', form.getValues('RunbookTasks'))
                    } else {
                        console.log('[RunbookTab] No tasks found in data')
                        form.setValue('RunbookTasks', [])
                    }
                })
                .catch((error) => {
                    console.error('Error fetching runbook info:', error)
                    setRunbookInfo(null)
                    form.setValue('RunbookTasks', [])
                    setLocalTasks([])
                })
        } else if (!isEditing) {
            setRunbookInfo(null)
            form.setValue('RunbookTasks', [])
            setLocalTasks([])
        }
    }, [isEditing, masterRunbook, form])

    return (
        <div className="space-y-6">
            {/* Top Row: Dropdown on left (only when creating), Instructions on right */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Left: Runbook Selection (only when creating) */}
                {!isEditing && (
                    <FormField
                        control={form.control}
                        name="MasterRunbook"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>{t('objects.masterRunbook')}</FormLabel>
                                <Select
                                    value={field.value?.toString() || ''}
                                    onValueChange={(value) => {
                                        field.onChange(value ? parseInt(value) : null)
                                    }}
                                >
                                    <FormControl>
                                        <SelectTrigger>
                                            <SelectValue placeholder={t('objects.selectMasterRunbook')} />
                                        </SelectTrigger>
                                    </FormControl>
                                    <SelectContent>
                                        {masterRunbooksData?.map((runbook: any) => (
                                            <SelectItem key={runbook.id} value={runbook.id.toString()}>
                                                {runbook.description}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                )}

                {/* Right: Runbook Instructions (show master runbook info when editing) */}
                <div className="space-y-2">
                    <FormLabel>{t('objects.runbookInformation')}</FormLabel>
                    <div
                        className="min-h-[150px] rounded-md border bg-muted p-4 overflow-auto"
                        dangerouslySetInnerHTML={{
                            __html: runbookInfo?.information || `<p class="text-muted-foreground">${t('objects.noRunbookSelected')}</p>`
                        }}
                    />
                </div>
            </div>

            {/* Bottom: Runbook Tasks Table */}
            <div className="space-y-2">
                <FormLabel>{t('objects.runbookTasks')}</FormLabel>
                {(() => {
                    console.log('[RunbookTab] Rendering - runbookTasks:', runbookTasks, 'length:', runbookTasks?.length, 'localTasks:', localTasks, 'localTasks length:', localTasks?.length)
                    return null
                })()}
                {(localTasks && localTasks.length > 0) || (runbookTasks && runbookTasks.length > 0) ? (
                    <RunbookTable
                        tasks={localTasks.length > 0 ? localTasks : runbookTasks}
                        onSave={(tasks) => {
                            // Update form state with new tasks (no separate save needed)
                            const tasksWithOffsets = calculateRunbookTimes(tasks)
                            form.setValue('RunbookTasks', tasksWithOffsets, { shouldValidate: false, shouldDirty: true })
                            setLocalTasks(tasksWithOffsets)
                        }}
                        autoUpdate={true} // Always auto-update form state
                        onAutoUpdateChange={() => {}} // No-op, always auto-update
                        isSaving={false}
                        hideToolbar={true} // Hide save/auto-update controls in form context
                    />
                ) : (
                    <div className="border rounded-md p-8 text-center text-muted-foreground">
                        {t('objects.noRunbookSelected')}
                    </div>
                )}
            </div>
        </div>
    )
}
